import pluralize from 'pluralize'
import { useSelector } from 'react-redux'
import { CoachesCoachingKind, CoachesCoachingKindDef, User } from '@src/types'
import { userSelector } from '@src/selectors/app'

type CoachDescriptorVariant =
  | 'coachTitle'
  | 'shortCoachTitle'
  | 'hubTitle'
  | 'shortHubTitle'
  | 'serviceTitle'

export interface CoachDescriptorOptions {
  variant?: CoachDescriptorVariant
  capitalized?: boolean
  plural?: boolean
}

type CoachDescriptorUser = Pick<User, 'primaryCoachingKind'> & {
  activeCoachingKinds?: User['activeCoachingKinds']
}

// TODO: Add coaching kinds to the backend client config and fetch these from the backend, instead
//
const DEFAULT_MNT_COACHING_KIND: CoachesCoachingKindDef = {
  key: CoachesCoachingKind.Mnt,
  coachTitle: 'Nutritionist',
  shortCoachTitle: 'Nutritionist',
  hubTitle: 'Coach Hub',
  shortHubTitle: 'Coach',
  clinical: true,
  insuranceReimbursable: true,
  serviceTitle: 'Nutrition',
  primary: true,
}

const DEFAULT_STRESS_COACHING_KIND: CoachesCoachingKindDef = {
  key: CoachesCoachingKind.StressManagement,
  coachTitle: 'Stress Management Coach',
  shortCoachTitle: 'Coach',
  hubTitle: 'Coach Hub',
  shortHubTitle: 'Coach',
  clinical: true,
  insuranceReimbursable: true,
  serviceTitle: 'Stress Management',
  primary: false,
}

const DEFAULT_HEALTH_COACHING_KIND: CoachesCoachingKindDef = {
  key: CoachesCoachingKind.HealthCoaching,
  coachTitle: 'Health Coach',
  shortCoachTitle: 'Coach',
  hubTitle: 'Coach Hub',
  shortHubTitle: 'Coach',
  clinical: false,
  insuranceReimbursable: false,
  serviceTitle: 'Health Coaching',
  primary: true,
}

class CoachDescriptor {
  private coachingKind?: CoachesCoachingKindDef
  private options: CoachDescriptorOptions

  constructor(coachingKind?: CoachesCoachingKindDef, options: CoachDescriptorOptions = {}) {
    this.coachingKind = coachingKind
    this.options = options
  }

  get shortTitle(): CoachDescriptor {
    return new CoachDescriptor(this.coachingKind, { ...this.options, variant: 'shortCoachTitle' })
  }

  get hubTitle(): CoachDescriptor {
    return new CoachDescriptor(this.coachingKind, { ...this.options, variant: 'hubTitle' })
  }

  get shortHubTitle(): CoachDescriptor {
    return new CoachDescriptor(this.coachingKind, { ...this.options, variant: 'shortHubTitle' })
  }

  get serviceTitle(): CoachDescriptor {
    return new CoachDescriptor(this.coachingKind, { ...this.options, variant: 'serviceTitle' })
  }

  get capitalized(): CoachDescriptor {
    return new CoachDescriptor(this.coachingKind, { ...this.options, capitalized: true })
  }

  get plural(): CoachDescriptor {
    return new CoachDescriptor(this.coachingKind, { ...this.options, plural: true })
  }

  toString(): string {
    const variant = this.options.variant ?? 'coachTitle'
    let descriptor = this.coachingKind ? this.coachingKind[variant] : ''

    if (!this.options.capitalized) {
      descriptor = descriptor.toLowerCase()
    }
    if (this.options.plural) {
      descriptor = pluralize(descriptor)
    }

    return descriptor
  }
}

const resolveCoachingKind = (coachingKind?: CoachesCoachingKind | CoachesCoachingKindDef) => {
  if (!coachingKind) {
    return undefined
  }

  if (typeof coachingKind !== 'string') {
    return coachingKind
  }

  switch (coachingKind) {
    case CoachesCoachingKind.Mnt:
      return DEFAULT_MNT_COACHING_KIND
    case CoachesCoachingKind.StressManagement:
      return DEFAULT_STRESS_COACHING_KIND
    case CoachesCoachingKind.HealthCoaching:
      return DEFAULT_HEALTH_COACHING_KIND
  }

  return undefined
}

export const getCoachingKindDescriptor = (
  coachingKind?: CoachesCoachingKind | CoachesCoachingKindDef | null,
  options: CoachDescriptorOptions = {},
) => {
  const resolvedKind = resolveCoachingKind(coachingKind || undefined)

  return new CoachDescriptor(resolvedKind, options)
}

export const getCoachDescriptor = (
  currentUser?: CoachDescriptorUser | null,
  options: CoachDescriptorOptions = {},
) => {
  const coachingKind = currentUser?.primaryCoachingKind

  return getCoachingKindDescriptor(coachingKind, options)
}

export const useCoachDescriptor = (options?: CoachDescriptorOptions) => {
  const user = useSelector(userSelector)

  return getCoachDescriptor(user, options)
}
